// src/components/common/PdfPreviewModal.js
import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

// Read-only in-browser PDF preview, fed from an authenticated endpoint that
// returns the PDF bytes directly (not a pre-existing URL) - always fetched
// as a blob with the auth header attached, since a plain <embed src="/api/...">
// can't carry an Authorization header itself. #toolbar=0&navpanes=0 hides the
// browser's native PDF viewer chrome (print/download icons) - this is a
// best-effort UI deterrent, not a hard guarantee against saving the
// rendered content, same limitation any in-browser viewer has.
export default function PdfPreviewModal({ show, onClose, title, fetchUrl, method = 'GET', body }) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !fetchUrl) return undefined;
    setLoading(true);
    setError('');
    let currentUrl = null;
    let cancelled = false;

    const request = method === 'POST'
      ? axios.post(fetchUrl, body, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      : axios.get(fetchUrl, {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

    request
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: 'application/pdf' });
        currentUrl = URL.createObjectURL(blob);
        setObjectUrl(currentUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        const backendMessage = typeof err.response?.data === 'string' ? err.response.data : null;
        setError(backendMessage || 'Unable to load preview.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
    // Depend on a stringified snapshot of body, not the object reference
    // itself - a caller passing an inline object literal (e.g. `body={{x}}`)
    // gets a new reference every render regardless of content, which would
    // otherwise re-trigger this effect (re-fetch, new blob URL, reloaded
    // <embed>) on every unrelated re-render of the caller - a visible
    // flicker if the caller re-renders frequently (e.g. a ticking clock).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, fetchUrl, method, JSON.stringify(body)]);

  const handleClose = () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    setObjectUrl(null);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title || 'Preview'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '70vh', padding: 0 }}>
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {!loading && error && (
          <Alert variant="danger" className="m-3">{error}</Alert>
        )}
        {!loading && !error && objectUrl && (
          <embed
            src={`${objectUrl}#toolbar=0&navpanes=0`}
            type="application/pdf"
            width="100%"
            height="700px"
            style={{ display: 'block' }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
}
